
import Vue from 'vue'
import { apiService } from '@/service/api.service'
import JourneyTreeNode from '@/components/user-journey/JourneyTreeNode.vue'

export default Vue.extend({
  name: 'UserJourney',
  components: { JourneyTreeNode },

  provide (): any {
    return { journey: this }
  },

  data () {
    return {
      tab: 'registered' as 'registered' | 'prospects',
      search: '',
      selectedId: null as string | null,
      selectedNodeId: null as string | null,
      drawer: false,
      drawerNode: null as any,
      registeredUsers: [] as any[],
      prospects: [] as any[],
      loadingUsers: false,
      selectedDetail: null as any,
      loadingDetail: false,
      searchTimer: null as any,
      collapseLicTick: 0,
      expandTick: 0,
      segment: '',
      segments: [
        { text: 'All users', value: '', desc: 'All registered users' },
        { text: 'Never activated', value: 'never_activated', desc: 'Registered but never activated a licence' },
        { text: 'Stuck at device', value: 'stuck_device', desc: 'Has a licence but no device connected yet' },
        { text: 'Stuck at job', value: 'stuck_job', desc: 'Connected a device but never started a job' },
        { text: 'Inactive 30d+', value: 'inactive', desc: 'No login in the last 30 days' },
        { text: 'Fully onboarded', value: 'onboarded', desc: 'Completed every step (started a job)' }
      ],
      confirmResend: false,
      resending: false,
      selectedOrderId: null as number | null,
      reminder: { show: false, orderId: null as number | null, lang: 'tr', olderThanDays: 10, loading: false, mode: '', result: null as any },
      snack: { show: false, text: '', color: 'success' }
    }
  },

  computed: {
    activeSegmentDesc (): string {
      const s = this.segments.find((x: any) => x.value === this.segment)
      return s && s.value ? s.desc : ''
    },
    isProspectSelected (): boolean {
      return !!this.selectedId && this.selectedId.startsWith('order-')
    }
  },

  watch: {
    search () {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.loadUsers(), 300)
    }
  },

  created () {
    this.loadUsers(true)
  },

  methods: {
    initial (s: string): string {
      return (s || '?').trim().charAt(0).toUpperCase()
    },

    async loadUsers (autoSelect = false) {
      this.loadingUsers = true
      try {
        const params: string[] = []
        if (this.search) params.push(`search=${encodeURIComponent(this.search)}`)
        if (this.segment) params.push(`segment=${encodeURIComponent(this.segment)}`)
        const q = params.length ? `?${params.join('&')}` : ''
        const res: any = await apiService.get(`admin-user-journey/users${q}`)
        this.registeredUsers = res.data.registered || []
        this.prospects = res.data.prospects || []
        if (autoSelect && !this.selectedId && this.registeredUsers.length) {
          this.selectUser(this.registeredUsers[0])
        }
      } catch {
        this.registeredUsers = []
        this.prospects = []
      } finally {
        this.loadingUsers = false
      }
    },

    async selectUser (u: any) {
      this.selectedId = u.id
      this.selectedOrderId = u.orderId || null
      this.selectedNodeId = null
      this.drawer = false
      this.loadingDetail = true
      this.selectedDetail = null
      try {
        const q = u.userId ? `user_id=${u.userId}` : `order_id=${u.orderId}`
        const res: any = await apiService.get(`admin-user-journey/detail?${q}`)
        this.selectedDetail = res.data.data
      } catch {
        this.selectedDetail = null
      } finally {
        this.loadingDetail = false
      }
    },
    // called by JourneyTreeNode via inject
    selectNode (node: any) {
      this.selectedNodeId = node.id
      this.drawerNode = node
      this.drawer = true
      this.confirmResend = false
    },
    collapseLicences () { this.collapseLicTick++ },
    expandAll () { this.expandTick++ },

    async doResend () {
      if (!this.drawerNode || !this.drawerNode.keyId) return
      this.resending = true
      try {
        const res: any = await apiService.post('admin-user-journey/resend-code', {
          activation_key_id: this.drawerNode.keyId,
          email: this.selectedDetail.email
        })
        if (res.data && res.data.status) {
          this.snack = { show: true, text: res.data.message || 'Code re-sent', color: 'success' }
        } else {
          this.snack = { show: true, text: (res.data && res.data.message) || 'Could not resend', color: 'error' }
        }
      } catch {
        this.snack = { show: true, text: 'Resend failed', color: 'error' }
      } finally {
        this.resending = false
        this.confirmResend = false
      }
    },
    openReminder (orderId: number | null) {
      this.reminder = { show: true, orderId, lang: 'tr', olderThanDays: 10, loading: false, mode: '', result: null }
    },

    async runReminder (dryRun: boolean) {
      this.reminder.loading = true
      this.reminder.mode = dryRun ? 'dry' : 'send'
      try {
        const body: any = { dry_run: dryRun, lang: this.reminder.lang }
        if (this.reminder.orderId) {
          body.ids = [this.reminder.orderId]
        } else {
          body.older_than_days = Number(this.reminder.olderThanDays) || 0
        }
        const res: any = await apiService.post('admin-user-journey/send-reminders', body)
        if (res.data && res.data.status) {
          this.reminder.result = res.data
          if (!dryRun) {
            this.snack = { show: true, text: `Sent ${res.data.sent}, skipped ${res.data.skipped}`, color: 'success' }
          }
        } else {
          this.snack = { show: true, text: (res.data && res.data.error) || 'Could not send reminders', color: 'error' }
        }
      } catch {
        this.snack = { show: true, text: 'Reminder request failed', color: 'error' }
      } finally {
        this.reminder.loading = false
      }
    },

    stepIcon (state: string): string {
      if (state === 'completed') return 'mdi-check'
      if (state === 'problem') return 'mdi-close'
      return ''
    },
    verdictIcon (tone: string): string {
      if (tone === 'success') return 'mdi-check-circle-outline'
      if (tone === 'danger') return 'mdi-alert-circle-outline'
      return 'mdi-alert-outline'
    },
    verdictColor (tone: string): string {
      if (tone === 'success') return '#16a34a'
      if (tone === 'danger') return '#dc2626'
      return '#d97706'
    }
  }
})
